import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { PROVIDERS } from "@/constants/testIds";

const CAT_LABEL = {
  plumbing: "Plomería", electrical: "Electricidad", painting: "Pintura",
  cleaning: "Limpieza", broker: "Inmobiliaria", accountant: "Contador",
  legal: "Legal", general: "General",
};

const emptyForm = () => ({ name: "", category: "general", phone: "", email: "", notes: "" });

export default function ProviderDialog({ open, onOpenChange, providers, onSaved }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const openNew = () => { setEditing(null); setForm(emptyForm()); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyForm(), ...p }); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/providers/${editing.id}`, form);
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/providers", form);
        toast.success("Proveedor creado");
      }
      setEditing(null); setForm(emptyForm());
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.name}?`)) return;
    try {
      await api.delete(`/providers/${p.id}`);
      toast.success("Eliminado");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={PROVIDERS.dialog} className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Proveedores</DialogTitle>
          <DialogDescription>Directorio de contactos para mantenimiento y servicios.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border/60 bg-card p-4">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">{editing ? "Editar" : "Nuevo"}</div>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Nombre</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CAT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Notas</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="flex gap-2">
                <Button type="submit" size="sm">{editing ? "Guardar" : <><Plus className="w-4 h-4 mr-1" /> Agregar</>}</Button>
                {editing && <Button type="button" variant="ghost" size="sm" onClick={openNew}>Cancelar</Button>}
              </div>
            </form>
          </div>

          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Directorio ({providers.length})</div>
            {providers.map((p) => (
              <div key={p.id} className="rounded-lg border border-border/60 bg-card p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {CAT_LABEL[p.category] || p.category} · {p.phone || "—"} · {p.email || "—"}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="text-rose-500 hover:text-rose-500" onClick={() => del(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            {!providers.length && <div className="text-xs text-muted-foreground py-4 text-center">Sin proveedores.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
